/**
 * B92 — the audit trail read (spec §11; B21's attempt rows): "who changed
 * what" for the tenant's back office. The authorizer writes one row per
 * attempt — allowed AND denied — before any operation runs; this door only
 * reads that history back. ADMIN-ONLY audience: administration is
 * deny-by-default ("catalog/channel/device/supply administration is
 * ADMIN-only until the spec says otherwise"), and the trail is security
 * evidence. Scope rides ledger:read per the B80 precedent — the trail is
 * books-adjacent evidence and a dedicated audit:read scope would be an
 * authorizer amendment (escalation). CAPABILITY stays walled: the
 * CAPABILITY-opened set remains the four report ops (pinned there).
 *
 * Rows carry EXACTLY the attempt-trail dimensions — B21 never stored
 * payloads or clientKeys, so this read cannot leak them. Cursor/window per
 * the house read pattern (refund.list's stance).
 */
import { z } from "zod";
import { readOp, zEpochMs, zId } from "./common.js";

const zAuditRow = z
  .object({
    /** The request id — one attempt, one row. */
    id: zId,
    at: zEpochMs,
    operation: z.string().min(1),
    // B94 — OPERATOR joins the trail: a platform seat reading across the
    // fence is stamped in the TENANT's own audit list, by name.
    principalKind: z.enum(["CUSTOMER", "STAFF", "RESELLER", "DEVICE", "CAPABILITY", "OPERATOR", "NONE"]),
    principalId: z.string().min(1),
    staffRole: z.string().min(1).optional(),
    /** B94 leg (d) — the operator behind a `login as` visit, on every row that visit wrote. */
    operatorId: z.string().min(1).optional(),
    grantId: z.string().min(1).optional(),
    /** B94 leg (d) — the operator's typed reason, on the visit's opening row. */
    note: z.string().min(1).max(400).optional(),
    outcome: z.enum(["ALLOWED", "DENIED"]),
    reason: z.string().min(1),
  })
  .strict();

export const auditList = readOp({
  name: "audit.list",
  permission: "ledger:read",
  principals: ["STAFF:ADMIN"],
  fields: {
    operation: z.string().min(1).optional(),
    outcome: z.enum(["ALLOWED", "DENIED"]).optional(),
    principalId: z.string().min(1).optional(),
    from: zEpochMs.optional(),
    to: zEpochMs.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.number().int().min(1).max(500).optional(),
  },
  response: z.object({ entries: z.array(zAuditRow), cursor: z.string().min(1).optional() }).strict(),
  exampleRequest: { outcome: "DENIED" },
  exampleResponse: {
    entries: [
      {
        id: "req-1",
        at: 1_780_000_000_000,
        operation: "refund.execute",
        principalKind: "STAFF",
        principalId: "staff-sell",
        staffRole: "SELLER",
        outcome: "DENIED",
        reason: "MISSING_PERMISSION",
      },
    ],
  },
});
